'use client'

import React, { useState, useEffect, useRef } from 'react'
import { ellipse, getBaseImageUrl, getCategoryHref, getMerchantHref } from '@/constants/hooks'
import { SearchCategories, SearchMerchant } from '@/services/dataTypes'
import { usePathname, useRouter, useSearchParams } from 'next/navigation'
import Link from 'next/link'
import Image from 'next/image'
import { Search, X, TrendingUp, FolderOpen, Store } from 'lucide-react'
import { apiSearchResult, apiGetAllKeywords } from '@/apis/user'

interface Props { companyId: string; mer_slug: string; slug_type: string; cat_slug: string; domain: string; isDark?: boolean; variant?: 'default' | 'hero'; placeholder?: string }

const SearchBar = ({ companyId, mer_slug, slug_type, cat_slug, domain, isDark = false, variant = 'default', placeholder }: Props) => {
    const searchParams = useSearchParams()
    const [search, setSearch] = useState<string>(new URLSearchParams(searchParams).get('query') || '')
    const [open, setOpen] = useState(false)
    const [tags, setTags] = useState<any[]>([])
    const [cats, setCats] = useState<SearchCategories[]>([])
    const [merchants, setMerchants] = useState<SearchMerchant[]>([])
    const [err, setErr] = useState('')
    const [recentSearches, setRecentSearches] = useState<string[]>([])
    const [mounted, setMounted] = useState(false)
    const pathName = usePathname()
    const router = useRouter()
    const inputRef = useRef<HTMLInputElement>(null)
    const wrapperRef = useRef<HTMLDivElement>(null)
    const cleanDomain = (!domain || domain === 'no domain in cookie' || domain.startsWith('localhost')) ? '' : domain

    useEffect(() => { setMounted(true) }, [])

    useEffect(() => {
        apiGetAllKeywords(companyId).then(r => r.data && setTags(r.data))
    }, [companyId])

    useEffect(() => { if (pathName === '/search') setOpen(false) }, [pathName])

    useEffect(() => {
        try {
            const saved = localStorage.getItem('recentSearches')
            if (saved) setRecentSearches(JSON.parse(saved).slice(0, 5))
        } catch { }
    }, [])

    useEffect(() => {
        function handleClickOutside(e: MouseEvent) {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
                setOpen(false)
            }
        }
        document.addEventListener('mousedown', handleClickOutside)
        return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [])

    const saveRecentSearch = (term: string) => {
        try {
            const updated = [term, ...recentSearches.filter(s => s !== term)].slice(0, 5)
            setRecentSearches(updated)
            localStorage.setItem('recentSearches', JSON.stringify(updated))
        } catch { }
    }

    useEffect(() => {
        if (!search.trim()) {
            setMerchants([]); setCats([]); setErr('')
            return
        }
        if (search.trim().length < 2) {
            setMerchants([]); setCats([])
            setErr('Type at least 2 characters.')
            return
        }

        const t = setTimeout(async () => {
            try {
                const res = await apiSearchResult(search, companyId)
                if (res.data?.merchants?.length || res.data?.categories?.length) {
                    setMerchants(res.data.merchants || [])
                    setCats(res.data.categories || [])
                    setErr('')
                } else {
                    setMerchants([]); setCats([])
                    setErr('No results found.')
                }
            } catch {
                setMerchants([]); setCats([])
                setErr('Something went wrong. Please try again.')
            }
        }, 400)
        return () => clearTimeout(t)
    }, [search, companyId])

    const handleSubmit = (term: string) => {
        if (!term.trim()) return
        saveRecentSearch(term.trim())
        setOpen(false)
        router.push(`/search?query=${encodeURIComponent(term.trim())}`)
    }

    const isHero = variant === 'hero'

    return (
        <div ref={wrapperRef} className="relative w-full z-40">
            <div className="relative flex items-center group">
                {isHero && (
                    <span className="absolute left-4 sm:left-5 text-slate-400 pointer-events-none z-10">
                        <Search size={18} />
                    </span>
                )}

                <input
                    ref={inputRef}
                    type="text"
                    placeholder={placeholder ?? (isHero ? "Search for stores, coupons or deals..." : "Search stores, coupons...")}
                    value={search}
                    autoComplete="off"
                    onChange={e => setSearch(e.target.value)}
                    onFocus={() => setOpen(pathName !== '/search')}
                    onKeyDown={e => { if (e.key === 'Enter') handleSubmit(search) }}
                    className={`w-full transition-all focus:outline-none ${
                        isHero
                            ? 'pl-11 sm:pl-12 pr-28 sm:pr-32 py-2.5 sm:py-3 rounded-full text-xs sm:text-sm md:text-[15px] font-medium text-slate-800 placeholder:text-slate-400 bg-white border border-white/40 shadow-[0_8px_24px_rgba(0,0,0,0.2)] focus:ring-4 focus:ring-[#00d69f]/30 focus:border-[#00d69f]'
                            : `pl-4 pr-14 py-2 rounded-full text-xs sm:text-sm shadow-xs focus:ring-4 focus:ring-[#00d69f]/20 focus:border-[#00d69f] ${
                                isDark
                                    ? 'bg-white/10 border border-white/15 text-white placeholder:text-white/50 hover:border-[#00d69f]/60'
                                    : 'bg-white border border-slate-200 text-slate-900 placeholder:text-slate-400 hover:border-[#00d69f]/60'
                            }`
                    }`} />

                {search && (
                    <button
                        type="button"
                        onClick={() => { setSearch(''); setMerchants([]); setCats([]); setErr('') }}
                        className={`absolute text-slate-400 hover:text-slate-600 transition-colors cursor-pointer z-10 ${
                            isHero ? 'right-24 sm:right-28' : 'right-10'
                        }`}
                    >
                        <X size={15} />
                    </button>
                )}

                {isHero ? (
                    <button
                        type="button"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => handleSubmit(search)}
                        aria-label="Search"
                        className="absolute right-1 top-1/2 -translate-y-1/2 px-4 sm:px-5 py-2 sm:py-2.5 bg-[#00d69f] hover:bg-[#00c28f] active:scale-95 text-[#071822] font-extrabold text-xs sm:text-sm rounded-full transition-all duration-200 flex items-center gap-1.5 shadow-sm cursor-pointer z-10"
                    >
                        <Search size={13} className="stroke-[2.5]" />
                        <span>Search</span>
                    </button>
                ) : (
                    <button
                        type="button"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => handleSubmit(search)}
                        aria-label="Search"
                        className="absolute right-1 top-1/2 -translate-y-1/2 w-7 h-7 bg-[#00d69f] hover:bg-[#00c28f] active:scale-95 text-[#071822] rounded-full transition-all duration-300 flex items-center justify-center shadow-xs cursor-pointer group/btn"
                    >
                        <Search size={12} className="text-[#071822] group-hover/btn:scale-110 transition-transform duration-300" />
                    </button>
                )}
            </div>

            {mounted && open && (
                <div className="absolute top-full left-0 mt-2 bg-white border border-slate-100 rounded-3xl shadow-[0_20px_50px_rgba(0,0,0,0.25)] z-50 w-full sm:w-[520px] max-w-[calc(100vw-2rem)] max-h-[480px] overflow-y-auto animate-fade-in">
                    <div className="p-4 sm:p-5 space-y-4">

                        {tags.length > 0 && !search.trim() && (
                            <div>
                                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2.5 flex items-center gap-1.5">
                                    <TrendingUp size={11} className="text-[#00a87a]" />
                                    <span>Popular Searches</span>
                                </p>
                                <div className="flex flex-wrap gap-2">
                                    {tags.slice(0, 15).map((tag, i) => (
                                        <button key={i} type="button" onClick={() => handleSubmit(tag)}
                                            className="text-xs font-semibold text-slate-600 bg-slate-50 border border-slate-100 px-3 py-1.5 rounded-full hover:border-[#00d69f] hover:text-[#00a87a] hover:bg-[#e6fcf5] transition-all duration-200 cursor-pointer">
                                            {tag}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}

                        {recentSearches.length > 0 && !search.trim() && (
                            <div>
                                <div className="flex items-center justify-between mb-2">
                                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Recent Searches</p>
                                    <button type="button"
                                        onClick={() => { setRecentSearches([]); try { localStorage.removeItem('recentSearches') } catch { } }}
                                        className="text-[10px] font-bold text-slate-400 hover:text-red-500 transition-colors flex items-center gap-0.5 cursor-pointer">
                                        <X size={10} /> Clear all
                                    </button>
                                </div>
                                <div className="space-y-1">
                                    {recentSearches.map((term, i) => (
                                        <div key={i} className="flex items-center justify-between group/item rounded-xl hover:bg-slate-50 transition-colors">
                                            <button type="button" onClick={() => handleSubmit(term)}
                                                className="flex-1 text-left px-3 py-2 text-xs font-semibold text-slate-700 hover:text-[#00a87a] transition-colors cursor-pointer">
                                                {term}
                                            </button>
                                            <button type="button"
                                                onClick={() => {
                                                    const updated = recentSearches.filter((_, idx) => idx !== i)
                                                    setRecentSearches(updated)
                                                    try { localStorage.setItem('recentSearches', JSON.stringify(updated)) } catch { }
                                                }}
                                                className="px-2 opacity-0 group-hover/item:opacity-100 transition-opacity text-slate-400 hover:text-red-500 cursor-pointer">
                                                <X size={12} />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {err && <p className="text-xs text-red-500 font-medium">{err}</p>}

                        {merchants.length > 0 && (
                            <div>
                                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2.5 flex items-center gap-1.5">
                                    <Store size={11} className="text-[#00a87a]" />
                                    <span>Stores ({merchants.length})</span>
                                </p>
                                <div className="space-y-1 max-h-48 overflow-y-auto">
                                    {merchants.slice(0, 6).map((m, i) => (
                                        <Link key={i} href={getMerchantHref(m, mer_slug, slug_type)} onClick={() => { saveRecentSearch(m.merchant_name); setOpen(false) }}
                                            className="flex items-center gap-3 p-2.5 rounded-2xl hover:bg-slate-50 transition-colors group">
                                            <div className="w-9 h-9 bg-white rounded-xl flex items-center justify-center border border-slate-100 group-hover:border-[#00d69f]/40 transition-colors flex-shrink-0 p-1">
                                                <Image src={getBaseImageUrl(cleanDomain, m.merchant_logo, '')} alt={m.merchant_name} width={56} height={56} unoptimized className="object-contain" />
                                            </div>
                                            <p className="text-xs font-bold text-slate-800 group-hover:text-[#00a87a] transition-colors">
                                                {ellipse(m.merchant_name, 30)}
                                            </p>
                                        </Link>
                                    ))}
                                </div>
                            </div>
                        )}

                        {cats.length > 0 && (
                            <div>
                                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2.5 flex items-center gap-1.5">
                                    <FolderOpen size={11} className="text-[#00a87a]" />
                                    <span>Categories ({cats.length})</span>
                                </p>
                                <div className="flex flex-wrap gap-2">
                                    {cats.slice(0, 6).map((c, i) => (
                                        <Link key={i} href={getCategoryHref(c, cat_slug, slug_type)} onClick={() => setOpen(false)}
                                            className="text-xs font-semibold text-slate-600 bg-slate-50 border border-slate-100 px-3 py-1.5 rounded-full hover:border-[#00d69f] hover:text-[#00a87a] hover:bg-[#e6fcf5] transition-all duration-200">
                                            {c.name}
                                        </Link>
                                    ))}
                                </div>
                            </div>
                        )}

                    </div>
                </div>
            )}

            <style dangerouslySetInnerHTML={{
                __html: `
                    @keyframes fade-in { from { opacity: 0; transform: translateY(-8px); } to { opacity: 1; transform: translateY(0); } }
                    .animate-fade-in { animation: fade-in 0.2s ease-out; }
                `
            }} />
        </div>
    )
}

export default SearchBar
